import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import type { Integration, IntegrationAccount } from '../integrationTypes';
import AccountEditor, { CredentialMark } from './AccountEditor';
import useCheckNow from './useCheckNow';
import { useIntegrations } from './IntegrationsContext';
import { accountActivity } from './integrationLabels';
import { AccountName, CardSection } from './parts';
import RemoveDialog from './RemoveDialog';
import { VerdictMark } from './Verdict';

const keyOf = (account: IntegrationAccount) => account.slug || '_default';

const AccountRow = ({ connection, account, first, canEdit, open, onOpen, onClose, onRemove }: {
    connection: Integration;
    account: IntegrationAccount;
    first: boolean;
    canEdit: boolean;
    open: boolean;
    onOpen: () => void;
    onClose: () => void;
    onRemove: () => void;
}) => {
    const check = useCheckNow(connection, account);
    const { trackedSince } = useIntegrations();
    const activity = accountActivity(account, trackedSince);
    const editable = canEdit && account.editable;
    return (
        <Box component="li" data-testid={`account-${keyOf(account)}`} sx={first ? undefined : { borderTop: 1, borderColor: 'divider' }}>
            <Box
                sx={{
                    display: 'grid',
                    // Fixed meta and action columns on md: every row lines up, whatever its buttons.
                    gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', md: 'minmax(0, 1fr) minmax(0, 2fr) 210px' },
                    gridTemplateAreas: { xs: '"name actions" "meta meta"', md: '"name meta actions"' },
                    alignItems: 'center',
                    columnGap: 2,
                    rowGap: 0.5,
                    py: 1,
                }}
            >
                <Box sx={{ gridArea: 'name', minWidth: 0 }}><AccountName name={account.display_name} slug={account.slug} /></Box>
                <Box
                    sx={{
                        gridArea: 'meta',
                        minWidth: 0,
                        display: { xs: 'flex', md: 'grid' },
                        flexWrap: 'wrap',
                        gridTemplateColumns: '130px minmax(0, 1fr) 160px',
                        alignItems: 'center',
                        columnGap: 2,
                        rowGap: 0.5,
                    }}
                >
                    <VerdictMark status={account.status} />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>{activity || ''}</Typography>
                    <CredentialMark credentials={account.credentials} />
                </Box>
                <Stack direction="row" spacing={0.5} sx={{ gridArea: 'actions', justifySelf: { xs: 'end', md: 'start' } }}>
                    {/* A row with no credentials of its own (its named accounts hold them) has nothing to check. */}
                    {editable && connection.can_check && account.credentials.keys.length > 0 && check.button}
                    {/* An open editor closes through its own Close, which asks before dropping unsaved changes. */}
                    {!open && (account.editable || account.settings) && (
                        <Button size="small" aria-label={`${editable ? 'Edit' : 'View'} ${account.display_name}`} onClick={onOpen}>
                            {editable ? 'Edit' : 'View'}
                        </Button>
                    )}
                    {editable && account.can_remove && !account.is_default && (
                        <Button size="small" color="inherit" sx={{ color: 'text.secondary' }} aria-label={`Remove ${account.display_name}`} onClick={onRemove}>
                            Remove
                        </Button>
                    )}
                </Stack>
            </Box>
            {check.result}
            {open && <AccountEditor connection={connection} account={account} canEdit={canEdit} onClose={onClose} />}
        </Box>
    );
};

/** One compact row per account, Default first (plan §1.4): name and slug, verdict, last accepted, credentials,
 *  [Check] (its own stored credentials), [Edit] and [Remove] (never on Default). Editors open inline under their
 *  rows, several at once. */
const AccountRows = ({ connection, canEdit }: { connection: Integration; canEdit: boolean }) => {
    const { api, reload, noteRemoval } = useIntegrations();
    const { enqueueSnackbar } = useSnackbar();
    const [open, setOpen] = React.useState<Set<string>>(new Set());
    const [adding, setAdding] = React.useState(false);
    const [removing, setRemoving] = React.useState<IntegrationAccount | null>(null);
    const toggle = (key: string, on: boolean) => setOpen((prev) => {
        const next = new Set(prev);
        if (on) {
            next.add(key);
        } else {
            next.delete(key);
        }
        return next;
    });

    return (
        <CardSection title="Accounts" testId="accounts">
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {connection.accounts.map((account, idx) => (
                    <AccountRow
                        key={keyOf(account)}
                        connection={connection}
                        account={account}
                        first={idx === 0}
                        canEdit={canEdit}
                        open={open.has(keyOf(account))}
                        onOpen={() => toggle(keyOf(account), true)}
                        onClose={() => toggle(keyOf(account), false)}
                        onRemove={() => setRemoving(account)}
                    />
                ))}
            </Box>
            {adding && <AccountEditor connection={connection} account={null} canEdit={canEdit} onClose={() => setAdding(false)} />}
            {canEdit && connection.can_add_account && !adding && (
                <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)} sx={{ mt: 0.5 }}>Add account</Button>
            )}
            <RemoveDialog
                open={!!removing}
                title={`Remove the account ${removing?.display_name || ''}?`}
                lines={[
                    `Its webhook addresses stop working at once. Trips and bookings already imported stay.`,
                    `Remove its webhooks in ${connection.name} too, or they keep being sent and refused.`,
                ]}
                confirmation={removing?.slug || ''}
                confirmLabel={`Type ${removing?.slug || ''} to confirm`}
                onRemove={async (typed) => {
                    if (!removing?.slug) {
                        return;
                    }
                    noteRemoval(`Account ${removing.display_name}`, await api.removeAccount(connection.id, removing.slug, typed));
                    enqueueSnackbar(`Account ${removing.display_name} removed`, { variant: 'success' });
                    // Not awaited: the dialog closes first, then the list drops the row.
                    reload();
                }}
                onClose={() => setRemoving(null)}
            />
        </CardSection>
    );
};

export default AccountRows;
